import type { CrudFilter, DataProvider } from "@refinedev/core";
import axios, { type AxiosError, type AxiosInstance } from "axios";

import { tinaAuthHeaders } from "@/lib/tina/auth";

const RESOURCE_PATHS: Record<string, string> = {
  "tina-runs": "/runs",
  "tina-items": "/events", // wire path stays /events — see spec §1.
  "tina-hitl": "/events", // HITL queue list reads from /events with status=hitl.
  // Enrichment parks get their own surface, but the rows are still `events` —
  // narrowed by `content_type=enrichment_*`, which is the opt-in that lifts
  // `list_events`' scrape-class default.
  "tina-enrichment": "/events",
  // Event-refresh jobs are `events` rows too, but this reads `v_refresh_jobs`
  // rather than `events`: mode / derived outcome / proposal counts / page-gone
  // are jsonb projections `GET /events` returns only as raw `metadata`, and
  // `list_events` hides refresh rows behind its scrape-class default anyway.
  // The `tina-` prefix fallback below already resolves this path — the entry is
  // here because the map is where a reader looks for the wire contract.
  "tina-refresh-jobs": "/refresh-jobs",
  // The routed resource behind `/tina/refresh-review`. It reads the same view:
  // the page is `GET /refresh-jobs?status=hitl`. Mapped explicitly because the
  // prefix fallback would resolve it to `/refresh-review`, a route that does not
  // exist — a 404 rather than the empty page a wrong filter gives.
  "tina-refresh-review": "/refresh-jobs",
  // The venue-cache auditor's findings queue. Mapped explicitly for the same
  // reason `tina-refresh-review` is, and more sharply: the prefix fallback would
  // resolve this to `/venue-audit`, which is not a route — the collection lives
  // one segment deeper, at `/venue-audit/findings`, beside `/venue-audit/run`.
  "tina-venue-audit": "/venue-audit/findings",
  // The artist duplicate auditor's findings queue. Same reason as the entry above: the
  // prefix fallback would resolve this to `/artist-audit`, which is not a route.
  "tina-artist-audit": "/artist-audit/findings",
  // The area audit's findings queue (the venue audit job's nightly area step). Same
  // reason again: the prefix fallback would resolve this to `/area-audit`, not a route.
  "tina-area-audit": "/area-audit/findings",
  // The festival list: one row per festival with its computed status. Mapped explicitly: the prefix fallback would
  // resolve it to `/festival-pages`, which is not a route.
  "tina-festival-pages": "/festivals/pages",
  "tina-site-health": "/site-health/findings", // prefix fallback would resolve to /site-health, not a route.
  "tina-sites": "/sites",
  "tina-tag-rules": "/tag-rules",
  "tina-cost-subscriptions": "/cost-subscriptions",
  "tina-tag-suggestions": "/tag-suggestions",
  "tina-notification-rules": "/notification-rules",
  "tina-groups": "/site-groups",
};

const resourcePath = (resource: string): string => {
  if (RESOURCE_PATHS[resource]) return RESOURCE_PATHS[resource];
  if (resource.startsWith("tina-")) return `/${resource.slice(5)}`;
  return `/${resource}`;
};

const flattenFilters = (
  filters: CrudFilter[] | undefined,
): Record<string, unknown> => {
  if (!filters) return {};
  const out: Record<string, unknown> = {};
  for (const f of filters) {
    if (
      "field" in f &&
      f.operator === "eq" &&
      f.value !== undefined &&
      f.value !== null
    ) {
      out[f.field] = f.value;
    }
    // A list filter (`status in [..]`) goes out as repeated keys; an empty or cleared one is left out.
    if (
      "field" in f &&
      f.operator === "in" &&
      Array.isArray(f.value) &&
      f.value.length > 0
    ) {
      out[f.field] = f.value;
    }
  }
  return out;
};

/** One entry of FastAPI's `RequestValidationError` detail array. */
interface ValidationErrorItem {
  loc?: unknown;
  msg?: unknown;
}

/**
 * Renders an orchestrator error `detail` as text. Our own `HTTPException`s carry
 * a string, which is passed through untouched; FastAPI's own validation errors
 * carry a list of `{loc, msg, type}` objects, which used to stringify to
 * "[object Object]" and made every 422 undiagnosable.
 */
const renderErrorDetail = (detail: unknown): string => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((entry: unknown) => {
        if (typeof entry === "string") return entry;
        const { loc, msg } = (entry ?? {}) as ValidationErrorItem;
        if (typeof msg !== "string") return JSON.stringify(entry);
        // `loc` is ["body", "<field>", …]; the "body" prefix is noise here.
        const field = Array.isArray(loc)
          ? loc.filter((part) => part !== "body").join(".")
          : "";
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  return JSON.stringify(detail) ?? String(detail);
};

export const buildHttpClient = (apiUrl: string): AxiosInstance => {
  // FastAPI reads a list as repeated keys (`status=a&status=b`); axios's default `status[]=a` is never read by it.
  const instance = axios.create({
    baseURL: apiUrl,
    paramsSerializer: { indexes: null },
  });

  // Tina authenticates the curator by their Firebase ID token, read per request
  // so a refreshed token is used as soon as the SDK has one. See lib/tina/auth.
  instance.interceptors.request.use(async (config) => {
    const headers = await tinaAuthHeaders();
    if (headers.Authorization) {
      config.headers.set("Authorization", headers.Authorization);
    }
    return config;
  });

  // Surface the orchestrator's `{detail: ...}` error shape so
  // toHumanErrorMessage renders it. See spec §4.3.
  instance.interceptors.response.use(
    (r) => r,
    (error: AxiosError<{ detail?: unknown }>) => {
      if (error.response?.data?.detail) {
        const wrapped = new Error(
          renderErrorDetail(error.response.data.detail),
        ) as Error & {
          statusCode?: number;
        };
        wrapped.statusCode = error.response.status;
        return Promise.reject(wrapped);
      }
      return Promise.reject(error);
    },
  );
  return instance;
};

export const tinaDataProvider = (
  apiUrl: string,
  httpClient:
    | AxiosInstance
    | { get: any; post?: any; patch?: any; delete?: any } = buildHttpClient(
    apiUrl,
  ),
): DataProvider => ({
  getApiUrl: () => apiUrl,

  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const path = meta?.urlPath ? `/${meta.urlPath}` : resourcePath(resource);
    const pageSize = pagination?.pageSize ?? 50;
    const currentPage = pagination?.currentPage ?? 1;
    const offset = (currentPage - 1) * pageSize;

    const sortParam =
      sorters && sorters.length > 0
        ? `${sorters[0].field}_${sorters[0].order}`
        : undefined;

    const response = await (httpClient as any).get(`${apiUrl}${path}`, {
      params: {
        limit: pageSize,
        offset,
        ...(sortParam ? { sort: sortParam } : {}),
        ...flattenFilters(filters),
      },
    });

    const body = response.data as { items: unknown[]; total: number };
    return { data: body.items as any[], total: body.total };
  },

  getOne: async ({ resource, id, meta }) => {
    const path = meta?.urlPath ? `/${meta.urlPath}` : resourcePath(resource);
    const response = await (httpClient as any).get(`${apiUrl}${path}/${id}`);
    // Match IMS DP convention: getOne returns the raw body, not {data}.
    return response.data;
  },

  create: async ({ resource, variables, meta }) => {
    const path = meta?.urlPath ? `/${meta.urlPath}` : resourcePath(resource);
    const response = await (httpClient as any).post(
      `${apiUrl}${path}`,
      variables,
    );
    return { data: response.data };
  },

  update: async ({ resource, id, variables, meta }) => {
    const path = meta?.urlPath ? `/${meta.urlPath}` : resourcePath(resource);
    const response = await (httpClient as any).patch(
      `${apiUrl}${path}/${id}`,
      variables,
    );
    return { data: response.data };
  },

  deleteOne: async ({ resource, id, meta }) => {
    const path = meta?.urlPath ? `/${meta.urlPath}` : resourcePath(resource);
    const response = await (httpClient as any).delete(`${apiUrl}${path}/${id}`);
    return { data: response.data };
  },

  getMany: async ({ resource, ids, meta }) => {
    const path = meta?.urlPath ? `/${meta.urlPath}` : resourcePath(resource);
    const results = await Promise.all(
      ids.map((id) => (httpClient as any).get(`${apiUrl}${path}/${id}`)),
    );
    return { data: results.map((r: any) => r.data) };
  },

  custom: async ({ url, method, payload, query, headers, meta }) => {
    const targetUrl = url || apiUrl;
    const path = meta?.urlPath ? `/${meta.urlPath}` : "";
    const response = await (httpClient as any)({
      method: method ?? "get",
      url: `${targetUrl}${path}`,
      params: query,
      data: payload,
      headers,
    });
    return { data: response.data };
  },
});
